import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { MY_CARDS_ROUTES } from 'routes';

// Query params that are safe to carry from one /my-cards tab to the other.
//
// This is an ALLOWLIST, deliberately. The obvious alternative — carry everything
// except `sort` — is a deny-list of one, and it silently carries the next param
// someone adds that has the same hazard. Adding to this list should require
// deciding that the param means the same thing on both lists.
//
// `sort` is the param that established the hazard, and it is worse than a display
// glitch: useSortPreference reads a URL `sort` as authoritative over localStorage
// (useSortPreference.ts getInitialSort), so carrying `sort=alert_enabled-desc` from
// the have list into the want list makes the want list adopt a have-list-only sort
// field and then persist it to localStorage['wantListSort']. That is a permanent
// wrong stored preference, not something a reload clears.
const CARRIED_TAB_PARAMS = ['openBulkManage'] as const;

export const useMyCards = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Derived from the pathname on every render, never mirrored into state. This is
  // what makes browser back/forward, a full reload, and navigate('/my-cards/have-list')
  // from other screens all actually move the tab. An unknown or absent segment falls
  // back to 0 (want list), matching the `index` route on /my-cards.
  const currentTabIndex = MY_CARDS_ROUTES.indexOf(
    location.pathname.split('/')[2] ?? ''
  );
  const tabIndex = currentTabIndex === -1 ? 0 : currentTabIndex;

  const onTabIndexChange = useCallback(
    (index: number) => {
      const path = MY_CARDS_ROUTES[index];
      if (!path) return;

      const current = new URLSearchParams(location.search);
      const next = new URLSearchParams();
      CARRIED_TAB_PARAMS.forEach((key) => {
        const value = current.get(key);
        if (value !== null) next.set(key, value);
      });

      const nextSearch = next.toString();
      navigate({
        pathname: `/my-cards/${path}`,
        search: nextSearch ? `?${nextSearch}` : '',
      });
    },
    [navigate, location.search]
  );

  return {
    tabIndex,
    onTabIndexChange,
  };
};
